import React, { useState } from 'react';
import { experiences } from '../../data/portfolio';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import SectionHeader from '../SectionHeader/SectionHeader';
import { FiBriefcase, FiCalendar, FiMapPin, FiChevronRight, FiChevronLeft } from 'react-icons/fi';
import './Experience.css';

function MobileStepper({ activeIndex, total, goPrev, goNext, setActiveIndex, showTitle }) {
  return (
    <div className="exp__mobile-stepper">
      <button
        className="exp__stepper-btn"
        onClick={goPrev}
        disabled={activeIndex === 0}
        aria-label="Previous role"
      >
        <FiChevronLeft />
      </button>

      <div className="exp__stepper-info">
        {showTitle && (
          <span className="exp__stepper-title">{experiences[activeIndex].role}</span>
        )}
        <span className="exp__stepper-count">{activeIndex + 1} / {total}</span>
        <div className="exp__stepper-dots">
          {experiences.map((_, i) => (
            <span
              key={i}
              className={`exp__stepper-dot ${i === activeIndex ? 'exp__stepper-dot--active' : ''}`}
              onClick={() => setActiveIndex(i)}
            />
          ))}
        </div>
      </div>

      <button
        className="exp__stepper-btn"
        onClick={goNext}
        disabled={activeIndex === total - 1}
        aria-label="Next role"
      >
        <FiChevronRight />
      </button>
    </div>
  );
}

function Experience() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [wrapRef, wrapVisible] = useScrollReveal(0.15);
  const active = experiences[activeIndex];
  const total = experiences.length;

  const goPrev = () => setActiveIndex((i) => Math.max(0, i - 1));
  const goNext = () => setActiveIndex((i) => Math.min(total - 1, i + 1));

  return (
    <section className="experience section" id="experience">
      <div className="container">
        <SectionHeader title="Experience" subtitle="Where I've worked" />

        {/* Top stepper — mobile only, shows role title */}
        <MobileStepper
          activeIndex={activeIndex}
          total={total}
          goPrev={goPrev}
          goNext={goNext}
          setActiveIndex={setActiveIndex}
          showTitle
        />

        <div
          ref={wrapRef}
          className={`exp ${wrapVisible ? 'exp--visible' : ''}`}
        >
          {/* Left: Tab selector */}
          <div className="exp__tabs" role="tablist">
            {experiences.map((exp, i) => (
              <button
                key={exp.id}
                role="tab"
                aria-selected={i === activeIndex}
                className={`exp__tab ${i === activeIndex ? 'exp__tab--active' : ''}`}
                onClick={() => setActiveIndex(i)}
              >
                <span className="exp__tab-indicator" />
                <div className="exp__tab-content">
                  <span className="exp__tab-role">{exp.role}</span>
                  <span className="exp__tab-period">{exp.period}</span>
                </div>
                <FiChevronRight className="exp__tab-arrow" />
              </button>
            ))}
          </div>

          {/* Center: Journey progress bar */}
          <div className="exp__progress">
            <div className="exp__progress-track">
              <div
                className="exp__progress-fill"
                style={{ height: `${((activeIndex + 1) / total) * 100}%` }}
              />
              {experiences.map((_, i) => (
                <button
                  key={i}
                  className={`exp__progress-dot ${i <= activeIndex ? 'exp__progress-dot--active' : ''} ${i === activeIndex ? 'exp__progress-dot--current' : ''}`}
                  style={{ top: `${(i / (total - 1)) * 100}%` }}
                  onClick={() => setActiveIndex(i)}
                  aria-label={`Go to experience ${i + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Right: Detail panel */}
          <div className="exp__panel" role="tabpanel" key={active.id}>
            <div className="exp__panel-header">
              <div className="exp__panel-icon">
                <FiBriefcase />
              </div>
              <div>
                <h3 className="exp__panel-role">{active.role}</h3>
                <p className="exp__panel-company">@ {active.company}</p>
              </div>
            </div>

            <div className="exp__panel-meta">
              <span className="exp__panel-badge">
                <FiCalendar /> {active.period}
              </span>
              {active.location && (
                <span className="exp__panel-badge">
                  <FiMapPin /> {active.location}
                </span>
              )}
            </div>

            <ul className="exp__panel-list">
              {active.description.map((item, i) => (
                <li key={i} className="exp__panel-item" style={{ animationDelay: `${i * 0.08}s` }}>
                  <span className="exp__panel-bullet" />
                  {item}
                </li>
              ))}
            </ul>

            <div className="exp__panel-tech">
              {active.tech.map((t) => (
                <span key={t} className="exp__panel-tag">{t}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom stepper — mobile only, compact (no title) */}
        <MobileStepper
          activeIndex={activeIndex}
          total={total}
          goPrev={goPrev}
          goNext={goNext}
          setActiveIndex={setActiveIndex}
        />
      </div>
    </section>
  );
}

export default Experience;
